import { DownloadIcon } from "@chakra-ui/icons"
import {
  Button,
  Flex,
  Image,
  Text,
  useToast
} from "@chakra-ui/react"
import { useState } from "react"
import { buildDocumentUrl } from "../../common/documentUrl"
import { useReportError, verifyPdfAccessible } from "../../common/commonHooks/useReportError"
import { PDFIconImg, revitIconImg } from "../images"

const PDFIcon = (): JSX.Element => <Image src={PDFIconImg} />
const REVITIcon = (): JSX.Element => <Image src={revitIconImg} />

export interface ResolvedFile {
  url: string
  downloadUrl?: string | null
  fileName?: string | null
}

interface FileResourceRowProps {
  label: string
  mode: "pdf" | "revit"
  resolveFile: () => Promise<ResolvedFile | null>
}

// Reproduces the exact file-row interaction used on the Installation Instructions
// page and the Product Drawing & Revit Files page: a leading download-icon button,
// then an icon+label link that previews (PDF) or downloads (Revit) the file.
export const FileResourceRow = ({ label, mode, resolveFile }: FileResourceRowProps): JSX.Element => {
  const { reportError } = useReportError()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const toast = useToast()

  const downloadFromUrl = (url: string): void => {
    const link = document.createElement("a")
    link.href = url
    link.download = `${label}.${mode === "pdf" ? "pdf" : "rfa"}`
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast({ title: "Download started", status: "success", duration: 3000, isClosable: true })
  }

  const handleDownload = async (): Promise<void> => {
    setDownloadLoading(true)
    try {
      const resolved = await resolveFile()
      if (!resolved) throw new Error("File not found")
      downloadFromUrl(resolved.downloadUrl ?? resolved.url)
    } catch {
      reportError({ context: "Missing Report", detail: `Resource "${label}" failed to download` })
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleOpen = async (): Promise<void> => {
    if (mode === "revit") {
      await handleDownload()
      return
    }
    setPreviewLoading(true)
    try {
      const resolved = await resolveFile()
      if (!resolved) throw new Error("File not found")
      const accessible = await verifyPdfAccessible(resolved.url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Resource "${label}" returned access denied` })
        return
      }
      window.open(buildDocumentUrl(resolved.fileName, resolved.url) ?? resolved.url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Resource "${label}" failed to load` })
    } finally {
      setPreviewLoading(false)
    }
  }

  return (
    <>
      <Flex align="center" gap="4px">
        <Button
          variant="linkButton"
          p="0"
          minW="0"
          h="auto"
          display="flex"
          alignItems="center"
          flexShrink={0}
          onClick={() => { void handleDownload() }}
          isLoading={downloadLoading}
          aria-label={`Download ${label}`}
        >
          <DownloadIcon />
        </Button>
        <Button
          variant="linkButton"
          ml="3px"
          minH="22px"
          fontSize="15px"
          textAlign="left"
          lineHeight="18px"
          fontWeight="400"
          whiteSpace="unset"
          wordBreak="break-word"
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-start"
          onClick={() => { void handleOpen() }}
          isLoading={previewLoading}
        >
          {mode === "pdf" ? <PDFIcon /> : <REVITIcon />}
          <Text variant={["RButton2", "RButton2", "Button2Big"]} as="span" paddingLeft="8px">
            {label}
          </Text>
        </Button>
      </Flex>
    </>
  )
}
